import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Calendar, Clock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SlotCreneaux, DayOfWeek } from '@/types/order';
import { format, eachWeekOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';

interface RecurringSlotsProps {
  slots: SlotCreneaux[];
  onSlotsChange: (slots: SlotCreneaux[]) => void;
  startDate: string;
  onStartDateChange: (date: string) => void;
  deadline: string;
}

const JOURS: { value: DayOfWeek; label: string }[] = [
  { value: 'lundi', label: 'Lundi' },
  { value: 'mardi', label: 'Mardi' },
  { value: 'mercredi', label: 'Mercredi' },
  { value: 'jeudi', label: 'Jeudi' },
  { value: 'vendredi', label: 'Vendredi' },
  { value: 'samedi', label: 'Samedi' },
];

const HEURES: string[] = [];
for (let hour = 8; hour <= 19; hour++) {
  HEURES.push(`${hour.toString().padStart(2, '0')}:00`);
  if (hour < 19) {
    HEURES.push(`${hour.toString().padStart(2, '0')}:30`);
  }
}

const INCLUDED_WEEKS = 4;
const SUPPLEMENT_PER_VISIT = 79;

export function RecurringSlots({
  slots,
  onSlotsChange,
  startDate,
  onStartDateChange,
  deadline,
}: RecurringSlotsProps) {
  const addSlot = () => {
    onSlotsChange([
      ...slots,
      { jour: 'samedi', heureDebut: '10:00' },
    ]);
  };

  const removeSlot = (index: number) => {
    onSlotsChange(slots.filter((_, i) => i !== index));
  };

  const updateSlot = (index: number, field: keyof SlotCreneaux, value: string) => {
    const updated = [...slots];
    updated[index] = { ...updated[index], [field]: value };
    onSlotsChange(updated);
  };

  // Calcul heure de fin (+2h)
  const getEndTime = (startTime: string): string => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const endHour = (hours + 2) % 24;
    return `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Calcul des visites supplémentaires
  const calculateExtraVisits = (): number => {
    if (!deadline || !startDate || slots.length === 0) return 0;

    try {
      const deadlineDate = new Date(deadline);
      const start = new Date(startDate);
      const includedStartDate = new Date(deadlineDate);
      includedStartDate.setDate(includedStartDate.getDate() - INCLUDED_WEEKS * 7);

      if (start >= includedStartDate) return 0;

      const weeksBeforeIncluded = Math.ceil(
        (includedStartDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7)
      );

      return weeksBeforeIncluded * slots.length;
    } catch {
      return 0;
    }
  };

  // Calcul du nombre total de visites
  const calculateTotalVisits = (): number => {
    if (!deadline || !startDate || slots.length === 0) return 0;

    try {
      const deadlineDate = new Date(deadline);
      const start = new Date(startDate);

      const weeks = Math.ceil(
        (deadlineDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7)
      );

      return weeks * slots.length;
    } catch {
      return 0;
    }
  };

  const extraVisits = calculateExtraVisits();
  const totalVisits = calculateTotalVisits();
  const includedVisits = totalVisits - extraVisits;
  const supplementCost = extraVisits * SUPPLEMENT_PER_VISIT;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gold-600" />
          <h4 className="text-lg font-semibold text-navy-600">
            Créneaux de visite récurrents
          </h4>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addSlot}
        >
          <Plus className="w-4 h-4" />
          Ajouter
        </Button>
      </div>

      <p className="text-sm text-navy-400">
        Les 4 semaines précédant la vente sont incluses dans le forfait.
      </p>

      {/* Date de début */}
      <div>
        <label htmlFor="slotDateDebut" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <Calendar className="w-4 h-4 text-gold-600" />
          Date de début des visites
        </label>
        <Input
          id="slotDateDebut"
          type="date"
          value={startDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          className="max-w-md"
        />
        <p className="text-xs text-navy-400 mt-1">
          Dès que possible (aujourd'hui) ou choisir une date
        </p>
      </div>

      {/* Liste des créneaux */}
      <div className="space-y-3">
        <AnimatePresence>
          {slots.map((slot, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-3 p-4 bg-cream rounded-xl border border-navy-100"
            >
              {/* Jour */}
              <select
                value={slot.jour}
                onChange={(e) => updateSlot(index, 'jour', e.target.value)}
                className="px-3 py-2 rounded-lg border border-navy-200 bg-white text-navy-600 focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
              >
                {JOURS.map((j) => (
                  <option key={j.value} value={j.value}>
                    {j.label}
                  </option>
                ))}
              </select>

              {/* Heure début */}
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-navy-400" />
                <select
                  value={slot.heureDebut}
                  onChange={(e) => updateSlot(index, 'heureDebut', e.target.value)}
                  className="px-3 py-2 rounded-lg border border-navy-200 bg-white text-navy-600 focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
                >
                  {HEURES.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>

              {/* Flèche */}
              <span className="text-navy-400">→</span>

              {/* Heure fin (calculée) */}
              <span className="text-navy-600 font-medium">
                {getEndTime(slot.heureDebut)}
              </span>

              {/* Bouton supprimer */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeSlot(index)}
                className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                disabled={slots.length === 1}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Résumé des suppléments */}
      {extraVisits > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-amber-50 border border-amber-200 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <p className="text-sm font-semibold text-amber-900">
                {extraVisits} visite{extraVisits > 1 ? 's' : ''} supplémentaire{extraVisits > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-amber-700">
                Facturées {SUPPLEMENT_PER_VISIT}€ HTVA / visite
              </p>
              <p className="text-sm font-bold text-amber-900">
                💶 Supplément : {supplementCost}€ HTVA
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Aperçu des visites */}
      {totalVisits > 0 && deadline && startDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-white border border-navy-200 rounded-xl"
        >
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-gold-600" />
            <h5 className="font-semibold text-navy-600">
              Aperçu : {totalVisits} visite{totalVisits > 1 ? 's' : ''} sur {Math.ceil((new Date(deadline).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 7))} semaine{Math.ceil((new Date(deadline).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 7)) > 1 ? 's' : ''}
            </h5>
          </div>

          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gold-500 rounded" />
              <span className="text-xs text-navy-600">
                Incluses ({includedVisits})
              </span>
            </div>
            {extraVisits > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-400 rounded" />
                <span className="text-xs text-navy-600">
                  Supplément ({extraVisits})
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {generateVisitDates(slots, startDate, deadline).slice(0, 6).map((visit, i) => (
              <div
                key={i}
                className={`text-xs px-3 py-1.5 rounded-lg ${
                  visit.isExtra
                    ? 'bg-amber-100 text-amber-800 border border-amber-300'
                    : 'bg-gold-100 text-gold-800 border border-gold-300'
                }`}
              >
                {visit.label}
              </div>
            ))}
            {totalVisits > 6 && (
              <div className="text-xs px-3 py-1.5 rounded-lg bg-navy-100 text-navy-600">
                +{totalVisits - 6} autres...
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

// Helper pour générer les dates de visite
function generateVisitDates(
  slots: SlotCreneaux[],
  startDate: string,
  deadline: string
): { label: string; isExtra: boolean }[] {
  if (!startDate || !deadline || slots.length === 0) return [];

  try {
    const start = new Date(startDate);
    const end = new Date(deadline);
    const deadlineDate = new Date(deadline);
    const includedStartDate = new Date(deadlineDate);
    includedStartDate.setDate(includedStartDate.getDate() - INCLUDED_WEEKS * 7);

    const visits: { label: string; isExtra: boolean }[] = [];

    const weeks = eachWeekOfInterval({ start, end }, { weekStartsOn: 1 });

    weeks.forEach((weekStart) => {
      slots.forEach((slot) => {
        const dayIndex = JOURS.findIndex((j) => j.value === slot.jour);
        const visitDate = new Date(weekStart);
        visitDate.setDate(visitDate.getDate() + dayIndex);

        if (visitDate >= start && visitDate <= end) {
          visits.push({
            label: `${format(visitDate, 'EEE d MMM', { locale: fr })} ${slot.heureDebut}`,
            isExtra: visitDate < includedStartDate,
          });
        }
      });
    });

    return visits.sort((a, b) => {
      const dateA = new Date(a.label);
      const dateB = new Date(b.label);
      return dateA.getTime() - dateB.getTime();
    });
  } catch {
    return [];
  }
}
